import { format } from 'date-fns';
import { useFormikContext } from 'formik';
import React, { useEffect, useRef, useState } from 'react';
import { Keyboard, Modal, Platform, TextInputProps, View } from 'react-native';
import { Calendar } from 'react-native-calendars';
import { TextInputMask, TextInputMaskProps } from 'react-native-masked-text';
import styled from 'styled-components/native';
import { TouchableWithoutFeedback } from 'react-native-gesture-handler';
import { Feather, Ionicons } from '@expo/vector-icons';

import Colors from '../../constants/Colors';
import { useFormContext } from './Form';
import DateTimePicker from '@react-native-community/datetimepicker';
import { B } from '../Typography';
import Spacer from '../Spacer';

export const toIsoBirthDate = (birthDate: string) => {
  const [month, day, year] = birthDate.split('/');
  return `${year}-${month}-${day}`;
};

export const parseLocalBirthDate = (value: string) => {
  const dateArr = value.split('/');
  if (dateArr.length !== 3 || dateArr[2].length !== 4) return value;
  const month = Number(dateArr[0]);
  const day = Number(dateArr[1]);
  const year = Number(dateArr[2]);
  if (![month, day, year].every(Number.isInteger)) return new Date('');
  const date = new Date(year, month - 1, day);
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return new Date('');
  }
  return date;
};

const FormInputView = styled.View`
  width: 100%;
`;

const LabelText = styled.Text<{ error?: boolean }>`
  font-family: 'DMSans-Bold';
  font-size: 16px;
  line-height: 20px;
  color: ${p => (p.error ? Colors.error : Colors.secondaryText)};
  margin-bottom: 2px;
`;

const StyledCoverView = styled.View`
  position: absolute;
  width: 100%;
  height: 100%;
`;

const StyledTouchableWithoutFeedback = styled(TouchableWithoutFeedback)`
  width: 100%;
  height: 100%;
`;

const StyledSpinnerView = styled.View`
  border-radius: 18px;
  align-items: center;
  justify-content: center;
  width: 340px;
  background-color: white;
`;

const StyledDateTimePicker = styled(DateTimePicker)`
  width: 100%;
`;

const StyledButton = styled.TouchableOpacity`
  width: 100%;
  max-width: 340px;
  background-color: white;
  border-radius: 15px;
  align-items: center;
  justify-content: center;
  height: 40px;
`;

const StyledView = styled.View`
  flex: 1;
  align-items: center;
  justify-content: center;
`;

const StyledTextInputView = styled.View<{
  minHeight?: number;
  error?: boolean;
  hasHelperText: boolean;
}>`
  background-color: #fff;
  min-height: ${p => (p.minHeight ? `${p.minHeight}px` : 'auto')};
  border: 1px solid ${p => (p.error ? Colors.error : Colors.formInputBorder)};
  margin-bottom: ${p => (p.error || p.hasHelperText ? 4 : 12)}px;
  border-radius: 8px;
  flex-direction: row;
  height: 64px;
`;

const StyledModalView = styled.View`
  width: 100%;
  height: 100%;
  align-items: center;
  justify-content: center;
`;

const StyledModalBackgroundContainer = styled.TouchableWithoutFeedback`
  width: 100%;
  height: 100%;
  position: absolute;
  align-items: center;
  justify-content: center;
`;

const StyledModalBackground = styled.View`
  width: 100%;
  height: 100%;
  position: absolute;
  background-color: black;
  opacity: 0.3;
`;

const StyledTextInputMask = styled(TextInputMask)<{ empty?: boolean }>`
  font-family: ${p => (p.empty ? 'WorkSans-Regular' : 'DMSans-Bold')};
  font-size: 18px;
  padding: 16px;
  flex: 1;
` as React.FC<
  TextInputMaskProps & {
    ref: any;
    empty?: boolean;
  }
>;

const StyledCalendarView = styled.View`
  background-color: white;
  width: 100%;
  padding-top: 10px;
  padding-bottom: 10px;
  max-width: 350px;
  border-radius: 8px;
`;
const StyledCalendarIconView = styled.TouchableOpacity`
  align-self: center;
  margin-right: 8px;
`;

const HelperText = styled.Text`
  font-family: 'DMSans-Medium';
  font-size: 12px;
  line-height: 18px;
  color: ${Colors.formLabel};
  margin: 4px 4px 12px 4px;
`;

const ErrorText = styled(HelperText)`
  color: ${Colors.error};
`;

export type FormInputProps = TextInputProps & {
  name: string;
  label?: string;
  helperText?: string;
  showPlaceHolder?: boolean;
  birthDate?: boolean;
  autoSubmit?: boolean;
};

const FormDateInput: React.FC<FormInputProps> = ({
  name,
  label,
  helperText,
  showPlaceHolder,
  birthDate,
  autoSubmit,
  ...props
}) => {
  const textInputMaskRef = useRef<TextInputMask>(null!);
  const {
    handleChange,
    handleBlur,
    touched,
    errors,
    values,
    setFieldError,
    setErrors,
    setFieldTouched,
    dirty,
    isValid: isValidValues,
    handleSubmit
  } = useFormikContext<any>();
  const { setFocusedInput, focusNext } = useFormContext();
  const [height, setHeight] = useState<number>();
  const error = touched[name] && errors[name];
  const commitDateValue = (value: string) => {
    handleChange(name)(value);
    setFieldTouched(name, true, false);
  };
  const [showCalendar, setShowCalendar] = useState(false);
  const [showSpinner, setShowSpinner] = useState(false);
  const [selectedBirthDateIOS, setSelectedBirthDateIOS] = useState<number>();

  const getDate = () => {
    const date = new Date();
    date.setDate(date.getDate());
    const dateKey = format(date, 'yyyy-MM-dd');
    return dateKey;
  };

  const todayMarkStyle = {
    selected: true,
    selectedColor: '#8c538a',
    selectedTextColor: Colors.lightText
  };

  const selectedMarkStyle = {
    selected: true,
    selectedColor: Colors.primaryPurple,
    selectedTextColor: 'white'
  };

  const [markedDates, setMarkedDates] = useState({
    [getDate()]: todayMarkStyle
  });

  const toDateString = (date: string) => {
    const dateArr = date.split('/');
    if (dateArr.length === 3) {
      if (dateArr[2].length === 4) {
        return dateArr[2] + '-' + dateArr[0] + '-' + dateArr[1];
      }
    }
    return getDate();
  };

  const toDateInput = (date: string) => {
    const dateArr = date.split('-');
    if (dateArr.length === 3) {
      return dateArr[1] + '/' + dateArr[2] + '/' + dateArr[0];
    } else {
      return '';
    }
  };

  const toIsoInputValue = (date: string) => {
    const [month, day, year] = String(date || '').split('/');
    if (!month || !day || !year || year.length !== 4) return '';
    return `${year}-${month}-${day}`;
  };

  const toLocalDate = (date: string) => {
    const [month, day, year] = String(date || '').split('/');
    if (!month || !day || !year || year.length !== 4) return new Date();
    return new Date(Number(year), Number(month) - 1, Number(day));
  };

  useEffect(() => {
    if (autoSubmit && dirty && isValidValues) {
      setTimeout(() => {
        handleSubmit();
      }, 100);
    }
  }, [autoSubmit, dirty, handleSubmit, isValidValues]);

  return (
    <FormInputView>
      {label && <LabelText error={Boolean(error)}>{label}</LabelText>}
      {birthDate && Platform.OS === 'web' ? (
        <input
          type='date'
          value={toIsoInputValue(values[name])}
          max={format(new Date(), 'yyyy-MM-dd')}
          disabled={props.editable === false}
          onChange={event => {
            commitDateValue(toDateInput(event.target.value));
          }}
          onBlur={event => {
            handleBlur(name)(event);
          }}
          style={{
            WebkitAppearance: 'none',
            appearance: 'none',
            display: 'block',
            width: '100%',
            minWidth: 0,
            height: 64,
            boxSizing: 'border-box',
            padding: 16,
            marginBottom: error || helperText ? 4 : 12,
            backgroundColor: '#fff',
            border: `1px solid ${error ? Colors.error : Colors.formInputBorder}`,
            borderRadius: 8,
            fontFamily: values[name] ? 'DMSans-Bold' : 'WorkSans-Regular',
            fontSize: 18,
            color: values[name] ? Colors.darkText : Colors.placeholderColor
          }}
        />
      ) : (
        <StyledTextInputView
          minHeight={height}
          hasHelperText={Boolean(helperText)}
          error={Boolean(error)}
        >
          <StyledTextInputMask
            type={'datetime'}
            options={{
              format: 'MM/DD/YYYY',
              mask: 'MM/DD/YYYY'
            }}
            blurOnSubmit={false}
            ref={(textInput: TextInputMask) => {
              textInputMaskRef.current = textInput;
            }}
            {...props}
            value={values[name]}
            empty={!values[name]}
            onChangeText={text => {
              handleChange(name)(text);
              setMarkedDates({
                [toDateString(text)]: selectedMarkStyle
              });
            }}
            onBlur={event => {
              handleBlur(name)(event);
              setFocusedInput(undefined);
            }}
            onContentSizeChange={event => {
              setHeight(event.nativeEvent.contentSize.height);
            }}
            onSubmitEditing={() => {
              focusNext();
              Keyboard.dismiss();
            }}
            placeholder={showPlaceHolder ? 'MM/DD/YYYY' : ''}
            placeholderTextColor={Colors.placeholderColor}
          />
          {!birthDate && (
            <StyledCalendarIconView
              onPress={() => {
                setShowCalendar(true);
              }}
            >
              <Ionicons
                name='calendar'
                size={24}
                color={Colors.placeholderColor}
              />
            </StyledCalendarIconView>
          )}
          {birthDate && Platform.OS !== 'web' && (
            <StyledCoverView>
              <StyledTouchableWithoutFeedback
                onPress={() => {
                  setSelectedBirthDateIOS(undefined);
                  setShowSpinner(true);
                }}
              >
                <View />
              </StyledTouchableWithoutFeedback>
            </StyledCoverView>
          )}
        </StyledTextInputView>
      )}

      {error ? (
        <ErrorText>{error}</ErrorText>
      ) : (
        helperText && <HelperText>{helperText}</HelperText>
      )}
      {birthDate && Platform.OS === 'android' && showSpinner && (
        <DateTimePicker
          mode='date'
          value={toLocalDate(values[name])}
          display='spinner'
          onChange={date => {
            setShowSpinner(false);
            if (
              typeof date.nativeEvent.timestamp !== 'undefined' &&
              date.type === 'set'
            ) {
              commitDateValue(
                format(new Date(date.nativeEvent.timestamp), 'MM/dd/yyyy')
              );
            }
          }}
        />
      )}
      {birthDate && Platform.OS === 'ios' && (
        <Modal visible={showSpinner} transparent={true} animationType='fade'>
          <StyledModalBackgroundContainer
            onPress={() => {
              setShowSpinner(false);
              setSelectedBirthDateIOS(undefined);
            }}
          >
            <StyledView>
              <StyledModalBackground />
              <StyledSpinnerView>
                <StyledDateTimePicker
                  onChange={date => {
                    setSelectedBirthDateIOS(date.nativeEvent.timestamp);
                  }}
                  mode='date'
                  value={
                    typeof selectedBirthDateIOS !== 'undefined'
                      ? new Date(selectedBirthDateIOS)
                      : toLocalDate(values[name])
                  }
                  display='spinner'
                />
              </StyledSpinnerView>
              <Spacer size={1} />
              <StyledButton
                onPress={() => {
                  setShowSpinner(false);
                  setSelectedBirthDateIOS(undefined);
                }}
              >
                <B color='dark'>Cancel</B>
              </StyledButton>
              <Spacer size={1} />
              <StyledButton
                onPress={() => {
                  if (typeof selectedBirthDateIOS !== 'undefined') {
                    commitDateValue(
                      format(new Date(selectedBirthDateIOS), 'MM/dd/yyyy')
                    );
                  }
                  setShowSpinner(false);
                  setSelectedBirthDateIOS(undefined);
                }}
              >
                <B>Save</B>
              </StyledButton>
            </StyledView>
          </StyledModalBackgroundContainer>
        </Modal>
      )}
      <Modal visible={showCalendar} transparent={true} animationType='fade'>
        <StyledModalView>
          <StyledModalBackgroundContainer
            onPress={() => {
              setShowCalendar(false);
            }}
          >
            <StyledModalBackground />
          </StyledModalBackgroundContainer>
          <StyledCalendarView>
            <Calendar
              current={Object.keys(markedDates)[0]}
              markedDates={{
                [getDate()]: todayMarkStyle,
                ...markedDates
              }}
              enableSwipeMonths={true}
              hideExtraDays={true}
              onDayPress={(day: any) => {
                commitDateValue(toDateInput(day.dateString));
                setMarkedDates({
                  [day.dateString]: selectedMarkStyle
                });
                setShowCalendar(false);
              }}
              renderArrow={(direction: any) => (
                <Feather
                  name={direction === 'left' ? 'chevron-left' : 'chevron-right'}
                  size={28}
                />
              )}
              theme={{
                textDayFontFamily: 'DMSans-Bold',
                textMonthFontFamily: 'DMSans-Bold',
                textDayHeaderFontFamily: 'DMSans-Bold',
                textDayFontSize: 12,
                dayTextColor: Colors.primary,
                monthTextColor: Colors.primary,
                textMonthFontSize: 16
              }}
            />
          </StyledCalendarView>
        </StyledModalView>
      </Modal>
    </FormInputView>
  );
};

export default FormDateInput;
